import { BaseComponent } from '../core/BaseComponent';

export interface ModalOptions {
  backdrop: boolean;
  keyboard: boolean;
  focus: boolean;
  show: boolean;
  onShow?: (instance: Modal) => void;
  onHide?: (instance: Modal) => void;
  onShown?: (instance: Modal) => void;
  onHidden?: (instance: Modal) => void;
}

export interface ModalState {
  showing: boolean;
}

export class Modal extends BaseComponent<ModalOptions, ModalState> {
  private backdrop: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];
  private previouslyFocusedElement: HTMLElement | null = null;
  private boundHandleEsc: (e: KeyboardEvent) => void;

  constructor(element: HTMLElement, options: Partial<ModalOptions> = {}) {
    super(element, options);
    this.boundHandleEsc = this.handleEsc.bind(this);
  }

  protected getDefaultOptions(): ModalOptions {
    return {
      backdrop: true,
      keyboard: true,
      focus: true,
      show: false
    };
  }
  
  protected init(): void {
    super.init();
    this.element.setAttribute('role', 'dialog');
    this.element.setAttribute('aria-modal', 'true');
    
    if (this.options.show) {
      this.show();
    }
  }
  
  protected bindEvents(): void {
    const closeBtns = this.element.querySelectorAll('[data-fz-dismiss="modal"]');
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.hide());
    });
    
    if (this.options.backdrop) {
      this.element.addEventListener('click', (e) => this.handleBackdropClick(e));
    }
  }
  
  public show(): void {
    if (this.state.showing) return;
    
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.updateState({ showing: true });
    
    if (this.options.backdrop) {
      this.createBackdrop();
    }
    
    this.element.style.display = 'flex';
    this.element.classList.remove('d-none');
    this.element.classList.add('show');
    document.body.style.overflow = 'hidden';
    
    if (this.options.keyboard) {
      document.addEventListener('keydown', this.boundHandleEsc);
    }

    if (this.options.focus) {
      this.trapFocus();
    }
    
    if (this.options.onShow) this.options.onShow(this);
    this.emit('show');
    
    requestAnimationFrame(() => {
      this.element.classList.add('fade-in');
      if (this.options.onShown) {
        setTimeout(() => this.options.onShown!(this), 300); // Animation duration
      }
      this.emit('shown');
    });
  }
  
  public hide(): void {
    if (!this.state.showing) return;
    
    this.updateState({ showing: false });
    this.element.classList.remove('show', 'fade-in');
    
    if (this.options.keyboard) {
      document.removeEventListener('keydown', this.boundHandleEsc);
    }

    setTimeout(() => {
      this.element.style.display = 'none';
      this.element.classList.add('d-none');
      document.body.style.overflow = '';
      
      if (this.backdrop) {
        this.backdrop.remove();
        this.backdrop = null;
      }
      
      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
      }
      
      if (this.options.onHidden) this.options.onHidden(this);
      this.emit('hidden');
    }, 300);

    if (this.options.onHide) this.options.onHide(this);
    this.emit('hide');
  }

  private handleEsc(e: KeyboardEvent): void {
    if (e.key === 'Escape') this.hide();
  }
  
  private createBackdrop(): void {
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'modal-backdrop';
    document.body.appendChild(this.backdrop);
  }
  
  private trapFocus(): void {
    const focusables = this.element.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    this.focusableElements = Array.from(focusables);
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }
  
  private handleBackdropClick(event: MouseEvent): void {
    if (event.target === this.element) {
      this.hide();
    }
  }

  private updateState(newState: Partial<ModalState>): void {
    this.state = { ...this.state, ...newState };
  }
}
